/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

export type JinjaFilterKind = 'select' | 'range' | 'temporal';

export interface GenerateJinjaFilterClauseInput {
  filterType?: string;
  valueType?: 'string' | 'temporal' | 'numeric';
  filterColumn: string;
  sqlColumn: string;
}

function normalizeName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function needsJinjaClause(
  filterColumn: string,
  sqlColumn: string,
): boolean {
  return normalizeName(filterColumn) !== normalizeName(sqlColumn);
}

export function resolveJinjaFilterKind(
  filterType?: string,
  valueType?: GenerateJinjaFilterClauseInput['valueType'],
): JinjaFilterKind {
  const ft = (filterType ?? '').toLowerCase();
  if (valueType === 'temporal' || ft.includes('time') || ft.includes('date')) {
    return 'temporal';
  }
  if (
    valueType === 'numeric' ||
    ft.includes('range') ||
    ft.includes('slider')
  ) {
    return 'range';
  }
  return 'select';
}

function escapeFilterColumnName(column: string): string {
  return column.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export function generateSelectInClause(
  filterColumn: string,
  sqlColumn: string,
): string {
  const filterArg = escapeFilterColumnName(filterColumn);
  return `AND ${sqlColumn} IN ({{ "'" + "','".join(filter_values('${filterArg}')) + "'" }})`;
}

export function generateRangeClause(
  filterColumn: string,
  sqlColumn: string,
): string {
  const filterArg = escapeFilterColumnName(filterColumn);
  return `{%- for filter in get_filters('${filterArg}', remove_filter=True) -%}
{%- if filter.get('op') == '>=' -%}
AND ${sqlColumn} >= {{ filter.val }}
{%- elif filter.get('op') == '<=' -%}
AND ${sqlColumn} <= {{ filter.val }}
{%- endif -%}
{%- endfor -%}`;
}

export function generateTemporalClause(sqlColumn: string): string {
  return `AND ${sqlColumn} >= '{{ from_dttm }}' AND ${sqlColumn} <= '{{ to_dttm }}'`;
}

export function generateJinjaFilterClause(
  input: GenerateJinjaFilterClauseInput,
): string {
  const kind = resolveJinjaFilterKind(input.filterType, input.valueType);
  if (kind === 'temporal') {
    return generateTemporalClause(input.sqlColumn);
  }
  if (kind === 'range') {
    return generateRangeClause(input.filterColumn, input.sqlColumn);
  }
  return generateSelectInClause(input.filterColumn, input.sqlColumn);
}
